import { ErrorState } from '@/components/ErrorState'
import {
  CreateProjectDialog,
  ProjectEmptyState,
  ProjectListView,
  ProjectLoadingState,
  ProjectsFilterBar,
  ProjectsHeader,
  ProjectsViewToggle,
  ProjectTileView,
} from '@/components/projects'
import { Pagination } from '@/components/ui'
import { usePagination, useProjects } from '@/hooks'
import { ALL_CLIENTS, useProjectFilters } from '@/hooks/useProjectFilters'
import { PROJECT_DEFAULT_SORT_DIRECTIONS, type ProjectSortColumn } from '@/types'
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

const SORT_STORAGE_KEY = 'projects-sort'
const SORT_PARAM_KEYS = ['sortBy', 'sort'] as const

export function ProjectsPage() {
  const { page, pageSize, paginationParams, setPage, setPageSize, resetPage } = usePagination()
  const { filters, setFilter, viewMode, setViewMode, apiFilters } = useProjectFilters()
  const [searchParams, setSearchParams] = useSearchParams()

  const persistSort = useCallback((params: URLSearchParams) => {
    const sortParams = new URLSearchParams()
    SORT_PARAM_KEYS.forEach(k => {
      const v = params.get(k)
      if (v) sortParams.set(k, v)
    })
    sessionStorage.setItem(SORT_STORAGE_KEY, sortParams.toString())
  }, [])

  useLayoutEffect(() => {
    if (SORT_PARAM_KEYS.some(k => searchParams.has(k))) return
    const saved = sessionStorage.getItem(SORT_STORAGE_KEY)
    if (!saved) return
    const savedParams = new URLSearchParams(saved)
    if (!Array.from(savedParams.keys()).length) return
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      savedParams.forEach((value, key) => next.set(key, value))
      return next
    }, { replace: true })
  }, [searchParams, setSearchParams])

  const sortByParam = searchParams.get('sortBy')
  const sortBy: ProjectSortColumn | null =
    sortByParam && Object.hasOwn(PROJECT_DEFAULT_SORT_DIRECTIONS, sortByParam)
      ? (sortByParam as ProjectSortColumn)
      : null
  const sortDirParam = searchParams.get('sort')
  const sortDirection: 'asc' | 'desc' =
    sortDirParam === 'asc' || sortDirParam === 'desc' ? sortDirParam : (sortBy ? PROJECT_DEFAULT_SORT_DIRECTIONS[sortBy] : 'desc')

  const queryFilters = useMemo(
    () => ({ ...apiFilters, ...(sortBy ? { sortBy, sortDirection } : {}) }),
    [apiFilters, sortBy, sortDirection]
  )
  const { data, isLoading, isError, refetch } = useProjects(paginationParams, queryFilters)

  const projects = data?.items ?? []
  const totalCount = data?.totalCount ?? 0

  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  useEffect(() => {
    resetPage()
  }, [filters.search, filters.type, filters.industry, filters.client, resetPage])

  const handleSortChange = (column: ProjectSortColumn) => {
    const nextDirection =
      column === sortBy ? (sortDirection === 'asc' ? 'desc' : 'asc') : PROJECT_DEFAULT_SORT_DIRECTIONS[column]
    const next = new URLSearchParams(searchParams)
    next.set('sortBy', column)
    next.set('sort', nextDirection)
    persistSort(next)
    setSearchParams(next, { replace: true })
    resetPage()
  }

  return (
    <div className="space-y-6">
      <ProjectsHeader onCreateProject={() => setCreateDialogOpen(true)} />

      <div className="flex items-center justify-between gap-4">
        <ProjectsFilterBar filters={filters} onFilterChange={setFilter} sortBy={sortBy} onClearAll={() => {
          setFilter('search', '')
          setFilter('type', 'all')
          setFilter('industry', 'all')
          setFilter('client', ALL_CLIENTS)
          const next = new URLSearchParams(searchParams)
          SORT_PARAM_KEYS.forEach(k => next.delete(k))
          persistSort(next)
          setSearchParams(next, { replace: true })
        }} />
        <ProjectsViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />
      </div>

      {isLoading ? (
        <ProjectLoadingState />
      ) : isError ? (
        <ErrorState
          title="Failed to load projects"
          description="Something went wrong while loading your projects."
          testId="projects-error"
          onRetry={() => refetch()}
        />
      ) : projects.length === 0 ? (
        <ProjectEmptyState />
      ) : viewMode === 'list' ? (
        <div className="rounded-md border">
          <ProjectListView 
            projects={projects} 
            sortBy={sortBy} 
            sortDirection={sortDirection} 
            onSortChange={handleSortChange}/>
          <Pagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      ) : (
        <div className="rounded-md border">
          <ProjectTileView projects={projects} />
          <Pagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}

      <CreateProjectDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />
    </div>
  )
}
